<template>
  <div class="enterprise-detail-page">
    <div class="container" v-loading="loading">
      <el-button @click="$router.back()" icon="el-icon-back" class="back-btn">返回</el-button>
      <el-button v-if="canEdit && enterprise" @click="showEditDialog" type="primary" class="edit-btn">编辑企业信息</el-button>

      <div v-if="enterprise">
        <!-- 企业基本信息 -->
        <el-card class="info-card">
          <div class="name-row">
            <h1 class="enterprise-name">{{ enterprise.name }}</h1>
            <div class="name-actions">
              <!-- name-actions left intentionally minimal; top-right button will handle view/generate -->
            </div>
          </div>

          <!-- 顶部右上角查看/生成按钮 -->
          <div class="top-right-actions" v-if="enterprise">
            <el-button type="primary" size="small" :loading="generatingDoc"
              @click="viewOrGenerateDocument">查看资质文件</el-button>
          </div>

          <div class="meta-row">
            <div class="meta-item">
              <span class="label">注册日期：</span>
              <span class="value">{{ enterprise.registrationDate }}</span>
            </div>
            <div class="meta-item" v-if="enterprise.creditCode">
              <span class="label">统一信用代码：</span>
              <span class="value">{{ enterprise.creditCode }}</span>
            </div>
          </div>

          <div class="info-grid">
            <div class="info-item" v-if="enterprise.creditRating">
              <span class="label">信用评级：</span>
              <span class="value">{{ enterprise.creditRating }}</span>
            </div>

            <div class="info-item" v-if="enterprise.legalRepresentative">
              <span class="label">法定代表人：</span>
              <span class="value">{{ enterprise.legalRepresentative }}</span>
            </div>

            <div class="info-item" v-if="enterprise.phone">
              <span class="label">联系电话：</span>
              <span class="value">{{ enterprise.phone }}</span>
            </div>

            <div class="info-item" v-if="enterprise.registeredCapital">
              <span class="label">注册资本：</span>
              <span class="value">{{ enterprise.registeredCapital }} 万元</span>
            </div>
          </div>

          <div class="info-section" v-if="enterprise.businessScope">
            <h3>业务范围</h3>
            <p>{{ enterprise.businessScope }}</p>
          </div>
          <div class="info-section" v-if="enterprise.services">
            <h3>提供服务</h3>
            <p>{{ enterprise.services }}</p>
          </div>
        </el-card>



        <!-- 工商变更记录 -->
        <el-card class="section-card" v-if="businessChanges && businessChanges.length > 0">
          <h2 class="section-title">工商变更记录</h2>
          <el-timeline>
            <el-timeline-item v-for="change in businessChanges" :key="change.id"
              :timestamp="formatDate(change.changeDate || change.applicationDate)" placement="top">
              <el-card>
                <h4>{{ getBusinessChangeTypeLabel(change.changeType) }}</h4>
                <p>{{ change.changeContent || change.description }}</p>
                <el-tag size="small" :type="getStatusTagType(change.status)">
                  {{ getStatusLabel(change.status) }}
                </el-tag>
              </el-card>
            </el-timeline-item>
          </el-timeline>
        </el-card>

        <!-- 交易数据统计 -->
        <el-card class="section-card" v-if="transactions && transactions.length > 0">
          <h2 class="section-title">交易概况</h2>

          <!-- 概览数据 -->
          <el-row :gutter="20" style="margin-bottom: 24px;">
            <el-col :span="8">
              <div class="stat-card">
                <div class="stat-icon stat-icon--blue"><i class="el-icon-coin"></i></div>
                <div class="stat-info">
                  <div class="stat-label">总成交金额</div>
                  <div class="stat-num">¥{{ formatAmount(totalRevenue) }}</div>
                </div>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="stat-card">
                <div class="stat-icon stat-icon--green"><i class="el-icon-s-order"></i></div>
                <div class="stat-info">
                  <div class="stat-label">成交订单数</div>
                  <div class="stat-num">{{ transactions.length }} 笔</div>
                </div>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="stat-card">
                <div class="stat-icon stat-icon--orange"><i class="el-icon-s-goods"></i></div>
                <div class="stat-info">
                  <div class="stat-label">购买服务种类</div>
                  <div class="stat-num">{{ serviceTypeCount }} 种</div>
                </div>
              </div>
            </el-col>
          </el-row>

          <!-- 服务占比图 -->
          <div v-if="transactions.length > 1" ref="txPieChart" style="height: 280px; width: 100%;"></div>

          <!-- 流水明细表 -->
          <h3 style="margin: 20px 0 12px; font-size: 15px; color: #606266;">流水明细</h3>
          <el-table :data="transactions" style="width: 100%" size="small" stripe>
            <el-table-column prop="createdAt" label="成交时间" min-width="160">
              <template slot-scope="scope">
                {{ formatDate(scope.row.createdAt) }}
              </template>
            </el-table-column>
            <el-table-column prop="customerName" label="下单人" min-width="100">
              <template slot-scope="scope">
                {{ maskName(scope.row.customerName) }}
              </template>
            </el-table-column>
            <el-table-column prop="amount" label="交易金额" min-width="120">
              <template slot-scope="scope">
                <span style="color: #67c23a; font-weight: bold;">¥{{ formatAmount(scope.row.amount) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="serviceName" label="服务名称" min-width="140"></el-table-column>
          </el-table>
        </el-card>


      </div>

      <el-empty v-else-if="!loading" description="企业不存在"></el-empty>
    </div>

    <!-- 编辑对话框 -->
    <el-dialog title="编辑企业信息" :visible.sync="editDialogVisible" width="60%">
      <el-form :model="editForm" :rules="editRules" ref="editForm" label-width="120px">
        <el-form-item label="经营范围" prop="businessScope">
          <el-input type="textarea" :rows="4" v-model="editForm.businessScope"></el-input>
        </el-form-item>

        <el-form-item label="提供服务" prop="services">
          <el-input type="textarea" :rows="4" v-model="editForm.services"></el-input>
        </el-form-item>

        <template v-if="isAdmin">
          <el-form-item label="企业名称" prop="name">
            <el-input v-model="editForm.name"></el-input>
          </el-form-item>

          <el-form-item label="统一信用代码" prop="creditCode">
            <el-input v-model="editForm.creditCode"></el-input>
          </el-form-item>

          <el-form-item label="联系电话" prop="phone">
            <el-input v-model="editForm.phone"></el-input>
          </el-form-item>

          <el-form-item label="信用评级" prop="creditRating">
            <el-input v-model="editForm.creditRating"></el-input>
          </el-form-item>

          <el-form-item label="法定代表人" prop="legalRepresentative">
            <el-input v-model="editForm.legalRepresentative"></el-input>
          </el-form-item>

          <el-form-item label="注册资本" prop="registeredCapital">
            <el-input-number v-model="editForm.registeredCapital" :min="0" :precision="2"></el-input-number>
          </el-form-item>

          <el-form-item label="状态" prop="status">
            <el-select v-model="editForm.status">
              <el-option label="活跃" value="ACTIVE"></el-option>
              <el-option label="注销" value="INACTIVE"></el-option>
            </el-select>
          </el-form-item>
        </template>
      </el-form>

      <span slot="footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitEdit" :loading="submitting">保存</el-button>
      </span>
    </el-dialog>

    <!-- 文档弹窗 -->
    <el-dialog :visible.sync="docDialogVisible" width="600px" top="5vh" class="doc-preview-dialog"
      :before-close="() => { docDialogVisible = false }">
      <div slot="title" class="dialog-header">
        <span class="el-dialog__title">企业资质文件预览</span>
        <div class="dialog-header-actions">
          <el-button v-if="isAdmin" type="warning" size="mini" icon="el-icon-refresh" :loading="generatingDoc" @click="handleRegenerate">重新生成</el-button>
          <el-button type="success" size="mini" icon="el-icon-download" @click="handleDownloadDoc">下载</el-button>
        </div>
      </div>
      <div class="dialog-image-wrap" v-loading="generatingDoc">
        <el-image :src="docDialogImage" style="width:100%; height: auto; display: block;"
          :preview-src-list="[docDialogImage]">
          <div slot="error" class="image-error-slot">
            <i class="el-icon-picture-outline"></i>
            <p>文件加载失败或已被删除</p>
            <el-button v-if="isAdmin" type="primary" size="small" :loading="generatingDoc" @click="handleRegenerate">立即重新生成</el-button>
          </div>
        </el-image>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="docDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import api from '../api/config'
import * as echarts from 'echarts'

export default {
  name: 'EnterpriseDetail',
  data() {
    return {
      enterprise: null,
      businessChanges: [],
      transactions: [],
      changeRecords: [],
      loading: false,
      editDialogVisible: false,
      submitting: false,
      generatingDoc: false,
      docDialogVisible: false,
      docDialogImage: '',
      // restore reactive form model and rules so template bindings are safe
      editForm: {
        name: '',
        creditCode: '',
        creditRating: '',
        businessScope: '',
        services: '',
        legalRepresentative: '',
        phone: '',
        registeredCapital: 0,
        status: 'ACTIVE'
      },
      editRules: {
        businessScope: [{ required: true, message: '请输入经营范围', trigger: 'blur' }],
        services: [{ required: true, message: '请输入提供服务', trigger: 'blur' }]
      },
      txPieChart: null
    }
  },
  computed: {
    isLoggedIn() {
      return !!localStorage.getItem('token')
    },
    isAdmin() {
      return localStorage.getItem('userRole') === 'ADMIN'
    },
    canEdit() {
      return this.isLoggedIn
    },
    filteredChangeRecords() {
      if (!this.changeRecords) return []

      // 过滤掉系统字段和不重要的变更记录
      return this.changeRecords.filter(record => {
        // 隐藏系统字段
        const systemFields = ['id', 'createdAt', 'updatedAt', 'version', 'isDeleted']
        if (systemFields.includes(record.fieldName)) {
          return false
        }

        // 隐藏UPDATE类型的系统操作
        if (record.changeType === 'UPDATE' && !record.changeContent) {
          return false
        }

        return true
      })
    },
    totalRevenue() {
      return this.transactions.reduce((sum, o) => sum + (Number(o.amount) || 0), 0)
    },
    serviceTypeCount() {
      return new Set(this.transactions.map(o => o.serviceName).filter(Boolean)).size
    }
  },
  mounted() {
    this.loadEnterpriseDetail()
  },
  beforeDestroy() {
    if (this.txPieChart) this.txPieChart.dispose()
  },
  methods: {
    renderTxPieChart() {
      if (!this.$refs.txPieChart || this.transactions.length <= 1) return
      if (this.txPieChart) this.txPieChart.dispose()
      this.txPieChart = echarts.init(this.$refs.txPieChart)
      const serviceMap = {}
      this.transactions.forEach(o => {
        const k = o.serviceName || '其他'
        serviceMap[k] = (serviceMap[k] || 0) + (Number(o.amount) || 0)
      })
      const data = Object.entries(serviceMap).map(([name, value]) => ({ name, value }))
      this.txPieChart.setOption({
        tooltip: { trigger: 'item', formatter: '{b}: ¥{c} ({d}%)' },
        legend: { orient: 'vertical', left: 'left', top: 'middle' },
        series: [{
          name: '服务占比',
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['60%', '50%'],
          data,
          label: { formatter: '{b}\n¥{c}' },
          emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.4)' } }
        }]
      })
    },
    async loadEnterpriseDetail() {
      try {
        this.loading = true
        const id = this.$route.params.id
        const response = await api.get(`/enterprises/${id}`)
        const data = response.data

        this.enterprise = data.enterprise
        this.businessChanges = data.businessChanges || []
        this.transactions = data.transactions || []
        this.changeRecords = data.changeRecords || []
        this.$nextTick(() => {
          this.renderTxPieChart()
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('加载企业详情失败:', error)
        this.$message.error('加载企业详情失败')
      } finally {
        this.loading = false
      }
    },
    showEditDialog() {
      if (!this.isLoggedIn) {
        this.$message.warning('请先登录')
        this.$router.push('/login')
        return
      }

      this.editForm = {
        name: this.enterprise.name,
        creditCode: this.enterprise.creditCode,
        creditRating: this.enterprise.creditRating,
        businessScope: this.enterprise.businessScope,
        services: this.enterprise.services,
        legalRepresentative: this.enterprise.legalRepresentative,
        phone: this.enterprise.phone,
        registeredCapital: this.enterprise.registeredCapital,
        status: this.enterprise.status
      }
      this.editDialogVisible = true
    },
    async submitEdit() {
      this.$refs.editForm.validate(async (valid) => {
        if (!valid) {
          return false
        }

        try {
          this.submitting = true
          const id = this.$route.params.id
          await api.put(`/enterprises/${id}`, this.editForm)

          this.$message.success('更新成功')
          this.editDialogVisible = false
          this.loadEnterpriseDetail()
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('更新失败:', error)
          this.$message.error('更新失败')
        } finally {
          this.submitting = false
        }
      })
    },
    async generateDocument() {
      if (!this.isAdmin) {
        this.$message.warning('只有管理员可以生成企业文档')
        return false
      }
      try {
        this.generatingDoc = true
        const id = this.$route.params.id
        const res = await api.post(`/enterprises/${id}/generate-document`)
        if (res && res.success) {
          this.$message.success('企业文档已生成')
          // 更新 enterprise 数据
          await this.loadEnterpriseDetail()
          return true
        } else {
          this.$message.error(res.message || '生成失败')
          return false
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('生成文档失败:', error)
        this.$message.error('生成文档失败')
        return false
      } finally {
        this.generatingDoc = false
      }
    },
    // single entry for the top-right button: view if exists, otherwise generate (admin) then view
    async viewOrGenerateDocument() {
      if (!this.enterprise) return
      // if document exists, open preview
      if (this.enterprise.documentUrl) {
        this.viewDocument(this.$http.getFullImageUrl(this.enterprise.documentUrl))
        return
      }

      // no document: if admin, generate then open; otherwise inform
      if (!this.isAdmin) {
        this.$message.info('企业文档尚未生成，只有管理员可以生成，请联系管理员或稍后重试')
        return
      }

      const ok = await this.generateDocument()
      if (ok && this.enterprise && this.enterprise.documentUrl) {
        this.viewDocument(this.$http.getFullImageUrl(this.enterprise.documentUrl))
      } else if (ok) {
        // if generate reported success but enterprise data not refreshed, try reloading and opening
        await this.loadEnterpriseDetail()
        if (this.enterprise && this.enterprise.documentUrl) {
          this.viewDocument(this.$http.getFullImageUrl(this.enterprise.documentUrl))
        } else {
          this.$message.error('生成成功，但未能获取文档，请稍后重试')
        }
      }
    },
    viewDocument(url) {
      if (!url) return
      this.docDialogImage = url
      this.docDialogVisible = true
    },
    async handleRegenerate() {
      const ok = await this.generateDocument()
      if (ok && this.enterprise && this.enterprise.documentUrl) {
        this.docDialogImage = this.$http.getFullImageUrl(this.enterprise.documentUrl)
      }
    },
    handleDownloadDoc() {
      if (!this.docDialogImage) return
      const filename = `${this.enterprise.name}_经营证明.png`
      this.$http.downloadImage(this.docDialogImage, filename)
    },
    getFieldLabel(fieldName) {
      const labels = {
        // 驼峰命名
        name: '企业名称',
        creditRating: '信用评级',
        creditCode: '统一信用代码',
        businessScope: '经营范围',
        services: '提供服务',
        legalRepresentative: '法定代表人',
        legalPersonIdCard: '法人身份证',
        registeredCapital: '注册资本',
        status: '状态',
        phone: '联系电话',
        logo: '企业Logo',
        payment_qr_code: '收款二维码',
        enterprise: '基础信息',
        licenseUrl: '营业执照',
        idCardFrontUrl: '身份证正面',
        idCardBackUrl: '身份证反面',

        // 下划线命名
        business_scope: '经营范围',
        credit_rating: '信用评级',
        legal_representative: '法定代表人',
        registered_capital: '注册资本',
        registration_date: '注册日期',
        contact_phone: '联系电话',
        contact_email: '联系邮箱',
        address: '企业地址',
        // 其他可能的字段
        enterpriseName: '企业名称',
        enterprise_name: '企业名称',
        companyType: '公司类型',
        company_type: '公司类型',
        operatingStatus: '经营状态',
        operating_status: '经营状态'
      }
      return labels[fieldName] || fieldName
    },
    getChangeTypeLabel(changeType) {
      const typeLabels = {
        'UPDATE': '信息更新',
        'CREATE': '创建记录',
        'DELETE': '删除记录',
        'BUSINESS_CHANGE': '工商变更',
        'STATUS_CHANGE': '状态变更',
        'INFO_UPDATE': '信息更新',
        'FIELD_UPDATE': '信息更新'
      }
      return typeLabels[changeType] || changeType
    },
    getChangeTagType(type) {
      const map = {
        'CREATE': 'success',
        'UPDATE': 'primary',
        'FIELD_UPDATE': 'primary',
        'DELETE': 'danger',
        'STATUS_CHANGE': 'warning'
      }
      return map[type] || 'info'
    },
    formatDate(dateString) {
      if (!dateString) return ''

      try {
        const date = new Date(dateString)
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return dateString
      }
    },
    getBusinessChangeTypeLabel(changeType) {
      const typeLabels = {
        'LEGAL_REPRESENTATIVE_CHANGE': '法定代表人变更',
        'REGISTERED_CAPITAL_CHANGE': '注册资本变更',
        'BUSINESS_SCOPE_CHANGE': '经营范围变更',
        'ADDRESS_CHANGE': '地址变更',
        'NAME_CHANGE': '企业名称变更',
        'SHAREHOLDER_CHANGE': '股东变更'
      }
      return typeLabels[changeType] || changeType
    },
    getStatusLabel(status) {
      const statusLabels = {
        'PENDING': '待处理',
        'PROCESSING': '处理中',
        'COMPLETED': '已完成',
        'REJECTED': '已拒绝',
        'CANCELLED': '已取消'
      }
      return statusLabels[status] || status
    },
    getStatusTagType(status) {
      const typeMap = {
        'PENDING': 'warning',
        'PROCESSING': 'info',
        'COMPLETED': 'success',
        'REJECTED': 'danger',
        'CANCELLED': 'info'
      }
      return typeMap[status] || 'info'
    },
    getBusinessTypeLabel(type) {
      const typeMap = {
        'PAYMENT': '支付',
        'REFUND': '退款',
        'RECHARGE': '充值',
        'WITHDRAW': '提现',
        'SERVICE_FEE': '服务费'
      }
      return typeMap[type] || type
    },
    formatAmount(amount) {
      if (amount == null) return '0.00'
      return Number(amount).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    maskName(name) {
      if (!name) return '-'
      if (name.length === 1) return name
      if (name.length === 2) return name[0] + '*'
      // 3字及以上：保留首尾各一字，中间用星号填充
      return name[0] + '*'.repeat(name.length - 2) + name[name.length - 1]
    }
  }
}
</script>

<style scoped lang="scss">
.change-record-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 12px 16px;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, .05);
    border-color: #dcdfe6;
  }

  .record-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;

    .type-tag {
      font-weight: bold;
    }

    .operator-name {
      font-size: 13px;
      color: #909399;

      i {
        margin-right: 4px;
      }
    }
  }

  .change-content {
    .field-item {
      display: flex;
      flex-direction: column;
      gap: 10px;

      .field-label {
        font-size: 14px;
        color: #606266;
        font-weight: 500;

        &::before {
          content: '';
          display: inline-block;
          width: 4px;
          height: 14px;
          background: #409eff;
          margin-right: 8px;
          vertical-align: middle;
          border-radius: 2px;
        }
      }

      .value-diff {
        display: flex;
        align-items: center;
        gap: 12px;
        background: #f8f9fb;
        padding: 8px 12px;
        border-radius: 4px;
        font-size: 13px;

        .old {
          color: #909399;
          text-decoration: line-through;
        }

        .new {
          color: #67c23a;
          font-weight: bold;
        }

        i {
          color: #409eff;
        }
      }
    }

    .desc {
      margin: 0;
      color: #333;
      font-size: 14px;
    }

    .reason {
      margin-top: 10px;
      font-size: 12px;
      color: #909399;
      background: #fdf6ec;
      padding: 4px 8px;
      border-radius: 2px;

      i {
        margin-right: 4px;
      }
    }
  }
}

.enterprise-detail-page {
  min-height: calc(100vh - 216px);
  background: #f5f5f5;
  padding: 40px 0;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fff;
  border-radius: 8px;
  padding: 16px 20px;
  box-shadow: 0 1px 6px rgba(0,0,0,.07);

  .stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    flex-shrink: 0;

    &--blue   { background: #ecf5ff; color: #409EFF; }
    &--green  { background: #f0f9eb; color: #67C23A; }
    &--orange { background: #fdf6ec; color: #E6A23C; }
  }

  .stat-info {
    .stat-label { font-size: 13px; color: #909399; margin-bottom: 4px; }
    .stat-num   { font-size: 22px; font-weight: bold; color: #303133; }
  }
}

.container {
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  /* ensure top-right positioning works */
}

.back-btn {
  margin-bottom: 20px;
  margin-right: 10px;
}

.edit-btn {
  margin-bottom: 20px;
}

.name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.name-actions {
  display: flex;
  gap: 8px;
}

.meta-row {
  display: flex;
  gap: 20px;
  margin-top: 8px;
  margin-bottom: 16px;
}

.info-card {
  margin-bottom: 20px;
  padding-bottom: 16px;

  .enterprise-name {
    font-size: 32px;
    margin-bottom: 0;
    color: #333;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 15px;
    margin-bottom: 20px;

    .info-item {
      .label {
        color: #999;
        margin-right: 8px;
      }

      .value {
        color: #333;
        font-weight: 500;
      }
    }
  }

  .info-section {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #eee;

    h3 {
      font-size: 18px;
      margin-bottom: 10px;
      color: #333;
    }

    p {
      color: #666;
      line-height: 1.6;
    }
  }
}

.document-section {
  display: flex;
  flex-direction: column;
}

.doc-thumbnail {
  width: 240px;
  height: 160px;
  cursor: pointer;
  border: 1px solid #eaeaea;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border-radius: 4px;
}

.dialog-image-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
}

.thumb-list {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.thumb-item {
  width: 100px;
  height: 80px;
  cursor: pointer;
  border: 1px solid #eee;
  border-radius: 4px;
}

.top-right-actions {
  position: absolute;
  right: 30px;
  top: 20px;
  z-index: 20;
}

@media screen and (max-width: 768px) {
  .enterprise-detail-page {
    padding: 20px 0;
  }

  .container {
    padding: 0 15px;
  }

  .info-card {
    .enterprise-name {
      font-size: 24px !important;
    }

    .info-grid {
      grid-template-columns: 1fr !important;
      gap: 10px;
    }
  }
}
.doc-preview-dialog {
  ::v-deep .el-dialog__header {
    padding: 20px;
    border-bottom: 1px solid #ebeef5;
    
    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-right: 30px;
    }
  }
}

.image-error-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  background: #f8f9fa;
  color: #909399;
  
  i {
    font-size: 40px;
    margin-bottom: 15px;
  }
  
  p {
    margin-bottom: 20px;
    font-size: 14px;
  }
}
</style>
